import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    MessageSquare, Send, ChevronLeft, ShieldCheck,
    Search, Briefcase, Shield, ArrowUpRight,
} from 'lucide-react';
import axios from 'axios';
import { Client } from '@stomp/stompjs';

const API = process.env.REACT_APP_API_BASE_URL || '';

function authHeader() {
    const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
}

function wsUrl() {
    if (API) return API.replace(/^http/, 'ws') + '/ws';
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return `${proto}://${window.location.host}/ws`;
}

// ── Time / date helpers ───────────────────────────────────────────────────────

function timeOf(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function dayLabel(iso) {
    const d = new Date(iso);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today - 86400000);
    if (d >= today) return 'Today';
    if (d >= yesterday) return 'Yesterday';
    const weekAgo = new Date(today - 6 * 86400000);
    if (d >= weekAgo) return d.toLocaleDateString('en-GB', { weekday: 'long' });
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Compact relative stamp for the conversation list ("now", "2m", "1h", "Tue").
function relTime(iso) {
    if (!iso) return '';
    const then = new Date(iso);
    const diff = Date.now() - then.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return then.toLocaleDateString('en-GB', { weekday: 'short' });
    return then.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

// Group consecutive messages by sender, inserting day dividers.
function buildBlocks(messages) {
    const blocks = [];
    let day = null;
    messages.forEach((m) => {
        const d = dayLabel(m.sentAt);
        if (d !== day) { day = d; blocks.push({ type: 'day', label: d }); }
        const from = m.mine ? 'me' : 'them';
        const last = blocks[blocks.length - 1];
        if (last && last.type === 'group' && last.from === from) last.items.push(m);
        else blocks.push({ type: 'group', from, items: [m] });
    });
    return blocks;
}

/* ---------------- Conversation row ---------------- */
function ConversationRow({ conv, active, onSelect }) {
    const hasPreview = !!conv.lastMessagePreview;
    return (
        <button
            className={'ch-conv' + (active ? ' is-active' : '') + (conv.unread > 0 ? ' is-unread' : '')}
            onClick={onSelect}
        >
            <span className="ch-avatar" aria-hidden>{conv.otherInitials}</span>
            <span className="ch-conv-main">
                <span className="ch-conv-top">
                    <span className="ch-conv-name">
                        <span>{conv.otherName}</span>
                        {conv.otherVerified && <ShieldCheck size={13} />}
                    </span>
                    <span className="ch-conv-time">{relTime(conv.lastMessageAt)}</span>
                </span>
                {hasPreview ? (
                    <span className="ch-conv-preview">
                        {conv.lastMessageMine && <span className="ch-conv-you">You: </span>}{conv.lastMessagePreview}
                    </span>
                ) : (
                    <span className="ch-conv-preview ch-conv-preview-empty">No messages yet</span>
                )}
                <span className="ch-conv-job">
                    <span className={'ch-conv-role-dot ' + (conv.youAre === 'worker' ? 'is-worker' : 'is-poster')} />
                    <Briefcase size={11} />
                    <span>{conv.jobTitle}</span>
                </span>
            </span>
            <span className="ch-conv-end">
                {conv.unread > 0 && <span className="ch-conv-badge">{conv.unread}</span>}
            </span>
        </button>
    );
}

/* ---------------- Thread pane ---------------- */
function ThreadPane({ conv, messages, loading, onBack, onSend, onViewJob }) {
    const [draft, setDraft] = useState('');
    const threadRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => { setDraft(''); }, [conv.id]);
    useEffect(() => {
        const el = threadRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [conv.id, messages.length]);

    const send = () => {
        const text = draft.trim();
        if (!text) return;
        onSend(text);
        setDraft('');
        if (inputRef.current) inputRef.current.style.height = 'auto';
    };
    const onInput = (e) => {
        setDraft(e.target.value);
        e.target.style.height = 'auto';
        e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
    };
    const onKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

    const isWorker = conv.youAre === 'worker';
    const blocks = buildBlocks(messages);
    let lastMineId = null;
    for (let i = messages.length - 1; i >= 0; i--) if (messages[i].mine) { lastMineId = messages[i].id; break; }

    return (
        <section className="ch-thread-pane">
            <header className="ch-head">
                <button className="ch-head-back" onClick={onBack} aria-label="Back to conversations"><ChevronLeft size={20} /></button>
                <span className="ch-avatar ch-avatar--sm" aria-hidden>{conv.otherInitials}</span>
                <div className="ch-head-info">
                    <div className="ch-head-name">
                        {conv.otherName}
                        {conv.otherVerified && <ShieldCheck size={14} className="ch-head-verified" aria-label="Verified" />}
                    </div>
                    <div className="ch-head-job">
                        <Briefcase size={12} />
                        <span>{conv.jobTitle}</span>
                    </div>
                </div>
                <div className="ch-head-actions">
                    <span className={'ch-role ' + (isWorker ? 'is-worker' : 'is-poster')}>
                        {isWorker ? "You're the worker" : "You're hiring"}
                    </span>
                    <button className="ix-btn ix-btn-outline ix-btn-sm" onClick={onViewJob}>View job <ArrowUpRight size={13} /></button>
                </div>
            </header>

            {messages.length === 0 && !loading ? (
                <div className="ch-empty">
                    <div className="ch-empty-icon"><MessageSquare size={28} /></div>
                    <h3 className="ch-empty-title">No messages yet</h3>
                    <p className="ch-empty-text">
                        Say hi to get started. You're connected because you're working together on <strong>{conv.jobTitle}</strong>.
                    </p>
                    <span className="ch-empty-hint"><Shield size={13} /> Messages stay private to the two of you</span>
                </div>
            ) : (
                <div className="ch-thread" ref={threadRef}>
                    {blocks.map((b, i) =>
                        b.type === 'day' ? (
                            <div className="ch-day" key={'d' + i}><span>{b.label}</span></div>
                        ) : (
                            <div className={'ch-group ch-group--' + (b.from === 'me' ? 'me' : 'them')} key={'g' + i}>
                                {b.from === 'them' && <div className="ch-name">{conv.otherName}</div>}
                                {b.items.map((m) => <div className="ch-bubble" key={m.id}>{m.content}</div>)}
                                <div className="ch-meta">
                                    <span>{timeOf(b.items[b.items.length - 1].sentAt)}</span>
                                    {b.from === 'me' && b.items[b.items.length - 1].id === lastMineId &&
                                        b.items[b.items.length - 1].read && (
                                            <span className="ch-seen"><ShieldCheck size={11} /> Seen</span>
                                        )}
                                </div>
                            </div>
                        )
                    )}
                </div>
            )}

            <div className="ch-composer">
                <div className="ch-input-wrap">
                    <textarea
                        ref={inputRef}
                        className="ch-input"
                        rows={1}
                        placeholder={`Message ${conv.otherName.split(' ')[0]}…`}
                        value={draft}
                        onChange={onInput}
                        onKeyDown={onKeyDown}
                    />
                </div>
                <button className="ch-send" onClick={send} disabled={!draft.trim()} aria-label="Send message">
                    <Send size={17} />
                </button>
            </div>
        </section>
    );
}

/* ---------------- App ---------------- */
const Chat = () => {
    const [conversations, setConversations] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [messagesByConv, setMessagesByConv] = useState({});
    const [loadingMsgs, setLoadingMsgs] = useState(false);
    const [search, setSearch] = useState('');
    const [mobileView, setMobileView] = useState('list');
    const navigate = useNavigate();
    const [params] = useSearchParams();

    // Refs so the WebSocket callback always sees the latest selection.
    const selectedIdRef = useRef(null);
    useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);

    const broadcastUnread = () => window.dispatchEvent(new Event('chat:unread'));

    // ── Initial load ───────────────────────────────────────────────────────────
    const loadConversations = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/api/chat/conversations`, { headers: authHeader() });
            setConversations(r.data || []);
            return r.data || [];
        } catch (e) {
            console.error('Failed to load conversations', e);
            return [];
        }
    }, []);

    const loadMessages = useCallback(async (convId) => {
        setLoadingMsgs(true);
        try {
            const r = await axios.get(`${API}/api/chat/conversations/${convId}/messages`, {
                headers: authHeader(), params: { page: 0, size: 50 },
            });
            // Backend returns a Page newest-first; reverse so oldest is at the top.
            const content = (r.data?.content || []).slice().reverse();
            setMessagesByConv((prev) => ({ ...prev, [convId]: content }));
        } catch (e) {
            console.error('Failed to load messages', e);
        } finally {
            setLoadingMsgs(false);
        }
    }, []);

    const markRead = useCallback(async (convId) => {
        try {
            await axios.post(`${API}/api/chat/conversations/${convId}/read`, {}, { headers: authHeader() });
            setConversations((prev) => prev.map((c) => (c.id === convId ? { ...c, unread: 0 } : c)));
            setMessagesByConv((prev) => prev[convId]
                ? { ...prev, [convId]: prev[convId].map((m) => (m.mine ? m : { ...m, read: true })) }
                : prev);
            broadcastUnread();
        } catch (e) { /* non-fatal */ }
    }, []);

    const select = useCallback((id) => {
        setSelectedId(id);
        setMobileView('thread');
        if (!messagesByConv[id]) loadMessages(id);
        markRead(id);
    }, [messagesByConv, loadMessages, markRead]);

    // First load — fetch list, optionally open a conversation passed via ?c=.
    useEffect(() => {
        (async () => {
            const list = await loadConversations();
            const requested = params.get('c');
            const startId = requested ? Number(requested) : (window.innerWidth > 760 ? list[0]?.id : null);
            if (startId && list.some((c) => c.id === startId)) select(startId);
        })();
    }, []);

    // ── WebSocket live delivery ──────────────────────────────────────────────────
    useEffect(() => {
        const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
        if (!token) return;

        const client = new Client({
            brokerURL: wsUrl(),
            connectHeaders: { Authorization: `Bearer ${token}` },
            reconnectDelay: 4000,
            onConnect: () => {
                client.subscribe('/user/queue/messages', (frame) => {
                    let msg;
                    try { msg = JSON.parse(frame.body); } catch { return; }
                    handleLiveMessage(msg);
                });
            },
        });
        client.activate();
        return () => { client.deactivate(); };
    }, []);

    const handleLiveMessage = useCallback((msg) => {
        const convId = msg.conversationId;
        const isActive = selectedIdRef.current === convId;

        // Append to the open thread (dedupe by id).
        setMessagesByConv((prev) => {
            if (!prev[convId]) return prev;
            if (prev[convId].some((m) => m.id === msg.id)) return prev;
            return { ...prev, [convId]: [...prev[convId], { ...msg, mine: false }] };
        });

        // Bump the conversation in the list; raise unread unless it's open.
        setConversations((prev) => {
            const idx = prev.findIndex((c) => c.id === convId);
            if (idx === -1) { loadConversations(); return prev; }
            const updated = {
                ...prev[idx],
                lastMessageAt: msg.sentAt,
                lastMessagePreview: msg.content,
                lastMessageMine: false,
                unread: isActive ? 0 : (prev[idx].unread || 0) + 1,
            };
            const next = [updated, ...prev.slice(0, idx), ...prev.slice(idx + 1)];
            return next;
        });

        if (isActive) markRead(convId);
        else broadcastUnread();
    }, [loadConversations, markRead]);

    // ── Send ─────────────────────────────────────────────────────────────────────
    const sendTo = async (convId, text) => {
        try {
            const r = await axios.post(`${API}/api/chat/conversations/${convId}/messages`,
                { content: text }, { headers: authHeader() });
            const msg = r.data;
            setMessagesByConv((prev) => ({ ...prev, [convId]: [...(prev[convId] || []), msg] }));
            setConversations((prev) => {
                const idx = prev.findIndex((c) => c.id === convId);
                if (idx === -1) return prev;
                const updated = {
                    ...prev[idx],
                    lastMessageAt: msg.sentAt,
                    lastMessagePreview: msg.content,
                    lastMessageMine: true,
                };
                return [updated, ...prev.slice(0, idx), ...prev.slice(idx + 1)];
            });
        } catch (e) {
            console.error('Failed to send message', e);
        }
    };

    // ── Render ─────────────────────────────────────────────────────────────────
    const totalUnread = conversations.reduce((n, c) => n + (c.unread || 0), 0);
    const q = search.trim().toLowerCase();
    const visible = conversations.filter((c) => {
        if (!q) return true;
        return (c.otherName || '').toLowerCase().includes(q) || (c.jobTitle || '').toLowerCase().includes(q);
    });
    const selected = conversations.find((c) => c.id === selectedId) || null;
    const selectedMsgs = selected ? (messagesByConv[selected.id] || []) : [];

    return (
        <div className="ch-page">
            <div className="ch-inbox" data-view={mobileView}>
                {/* LEFT — list */}
                <aside className="ch-list">
                    <div className="ch-list-head">
                        <div className="ch-list-titlerow">
                            <h1 className="ch-list-title">
                                Messages
                                {totalUnread > 0 && <span className="ch-list-count">{totalUnread}</span>}
                            </h1>
                        </div>
                        <label className="ch-search">
                            <Search size={15} />
                            <input
                                type="text"
                                placeholder="Search people or jobs"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </label>
                    </div>
                    <div className="ch-list-scroll">
                        {visible.length === 0 ? (
                            <div className="ch-list-empty">
                                {conversations.length === 0
                                    ? 'No conversations yet. Message a poster or an accepted worker to start one.'
                                    : `No conversations match “${search}”.`}
                            </div>
                        ) : (
                            visible.map((c) => (
                                <ConversationRow key={c.id} conv={c} active={c.id === selectedId} onSelect={() => select(c.id)} />
                            ))
                        )}
                    </div>
                </aside>

                {/* RIGHT — thread */}
                {selected ? (
                    <ThreadPane
                        key={selected.id}
                        conv={selected}
                        messages={selectedMsgs}
                        loading={loadingMsgs}
                        onBack={() => setMobileView('list')}
                        onSend={(text) => sendTo(selected.id, text)}
                        onViewJob={() => navigate(`/get-job/${selected.jobId}`)}
                    />
                ) : (
                    <section className="ch-thread-pane">
                        <div className="ch-blank">
                            <div className="ch-blank-icon"><MessageSquare size={26} /></div>
                            <p>Select a conversation to start chatting.</p>
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
};

export default Chat;
